import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { LiveSiteClicksOnMenuDropdownAndOptionInsideButtonName } from '@wix/restaurants-bi';

import { useOnlineOrdersStorage } from '../../storage';
import { useProcessedMenus } from '../../hooks/useProcessedMenus';
import {
  buildSectionTabs,
  buildMenuOptions,
} from '../../../../utils/menuNavigation';
import { MENU_QUERY_PARAMS } from '../../../../utils/constants';
import { useNavigationTopOffset } from '../../hooks/useNavigationTopOffset';

import { scrollToElementWithNavbarOffset } from './scrollToElement';

const OBSERVER_TOP_MARGIN = '-10%';
const OBSERVER_BOTTOM_MARGIN = '-80%';
const SECTION_SELECTOR = '[data-section-id]';

export const useHooks = () => {
  const {
    cartQuantities,
    openCart,
    reportMenuBarClick,
    reportMenuDropdownClick,
  } = useOnlineOrdersStorage();

  const { isEditor } = useEnvironment();
  const { navigationTopOffset, measureTopOffset } =
    useNavigationTopOffset(isEditor);
  const { processedMenus: menus } = useProcessedMenus();

  const hasMultipleMenus = menus.length > 1;

  const menuOptions = useMemo(() => buildMenuOptions(menus), [menus]);

  const [selectedMenuId, setSelectedMenuId] = useState('');

  const activeMenuId = selectedMenuId || menus[0]?.id;

  const sectionTabs = useMemo(
    () => buildSectionTabs(menus, activeMenuId),
    [menus, activeMenuId],
  );

  const [activeSectionId, setActiveSectionId] = useState('');

  const activeTabId = activeSectionId || sectionTabs[0]?.id;

  const isScrollingToSectionRef = useRef(false);
  const cancelScrollRef = useRef<(() => void) | undefined>();
  const scrollToElementWithLock = useCallback(
    (element: Element | null) => {
      cancelScrollRef.current?.();
      isScrollingToSectionRef.current = true;
      cancelScrollRef.current = scrollToElementWithNavbarOffset(
        element,
        measureTopOffset,
        () => {
          isScrollingToSectionRef.current = false;
        },
      );
    },
    [measureTopOffset],
  );

  useEffect(() => () => cancelScrollRef.current?.(), []);

  useScrollToMenuOrMenuSection({
    menus,
    sectionTabs,
    scrollToElementWithLock,
    setSelectedMenuId,
    setActiveSectionId,
  });

  useEffect(() => {
    const elements = document.querySelectorAll<HTMLElement>(SECTION_SELECTOR);

    if (elements.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (isScrollingToSectionRef.current) {
          return;
        }

        const intersecting = entries.filter((e) => e.isIntersecting);

        if (intersecting.length === 0) {
          return;
        }

        const canonical = intersecting.reduce((best, entry) =>
          entry.boundingClientRect.top < best.boundingClientRect.top
            ? entry
            : best,
        );

        const sectionId = (canonical.target as HTMLElement).dataset.sectionId;

        if (sectionId) {
          setActiveSectionId(sectionId);

          const parentMenu = menus.find((m) =>
            m.sections?.some((s) => s.id === sectionId),
          );

          if (parentMenu) {
            setSelectedMenuId(parentMenu.id);
          }
        }
      },
      {
        rootMargin: `${OBSERVER_TOP_MARGIN} 0px ${OBSERVER_BOTTOM_MARGIN} 0px`,
        threshold: 0,
      },
    );

    for (const el of elements) {
      observer.observe(el);
    }

    return () => observer.disconnect();
  }, [sectionTabs, menus]);

  const onSectionTabClick = useCallback(
    (sectionId: string) => {
      const sectionIndex = sectionTabs.findIndex((tab) => tab.id === sectionId);

      reportMenuBarClick({
        sectionId,
        sectionName: sectionTabs[sectionIndex]?.label,
        currentSectionNumber: sectionIndex >= 0 ? sectionIndex + 1 : undefined,
        numTotalSections: sectionTabs.length,
      });

      setActiveSectionId(sectionId);

      const sectionElement = document.querySelector(
        `[data-section-id="${sectionId}"]`,
      );

      scrollToElementWithLock(sectionElement);
    },
    [sectionTabs, reportMenuBarClick, scrollToElementWithLock],
  );

  const onMenuChange = useCallback(
    (menuId: string) => {
      const currentMenuIndex = menuOptions.findIndex(
        (option) => option.id === activeMenuId,
      );
      const selectedMenuIndex = menuOptions.findIndex(
        (option) => option.id === menuId,
      );

      reportMenuDropdownClick({
        buttonName:
          LiveSiteClicksOnMenuDropdownAndOptionInsideButtonName.DROPDOWN_MENU_OPTION,
        currentMenuId: activeMenuId,
        currentMenuName: menuOptions[currentMenuIndex]?.value,
        currentMenuNumber:
          currentMenuIndex >= 0 ? currentMenuIndex + 1 : undefined,
        menuId,
        menuName: menuOptions[selectedMenuIndex]?.value,
        menuNumber: selectedMenuIndex >= 0 ? selectedMenuIndex + 1 : undefined,
        numTotalMenus: menuOptions.length,
      });

      setSelectedMenuId(menuId);
      setActiveSectionId('');

      const menuElement = document.querySelector(`[data-menu-id="${menuId}"]`);

      scrollToElementWithLock(menuElement);
    },
    [
      menuOptions,
      activeMenuId,
      reportMenuDropdownClick,
      scrollToElementWithLock,
    ],
  );

  const cartCount = Object.values(cartQuantities).reduce(
    (sum, qty) => sum + qty,
    0,
  );

  return {
    hasMultipleMenus,
    menuOptions,
    activeMenuId,
    onMenuChange,
    sectionTabs,
    activeTabId,
    onSectionTabClick,
    cartCount,
    openCart: () => openCart(),
    navigationTopOffset,
  };
};

type ScrollToMenuOrSectionParams = {
  menus: ReturnType<typeof useOnlineOrdersStorage>['menus'];
  sectionTabs: { id: string; label: string }[];
  scrollToElementWithLock: (element: Element | null) => void;
  setSelectedMenuId: (id: string) => void;
  setActiveSectionId: (id: string) => void;
};

/**
 * On initial page load, scrolls to the menu/section specified in the URL params.
 * `?menuId=<id>` → scrolls to the menu.
 * `?sectionId=<id>&menuId=<id>` → scrolls to the section.
 */
const useScrollToMenuOrMenuSection = ({
  menus,
  sectionTabs,
  scrollToElementWithLock,
  setSelectedMenuId,
  setActiveSectionId,
}: ScrollToMenuOrSectionParams) => {
  const hasInitialScrolledRef = useRef(false);

  useEffect(() => {
    if (hasInitialScrolledRef.current || sectionTabs.length === 0) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const itemId = params.get(MENU_QUERY_PARAMS.itemId);
    const sectionId = params.get(MENU_QUERY_PARAMS.sectionId);
    const menuId = params.get(MENU_QUERY_PARAMS.menuId);

    if (itemId) {
      hasInitialScrolledRef.current = true;

      return;
    }

    hasInitialScrolledRef.current = true;

    if (sectionId) {
      const parentMenu = menus.find((m) =>
        m.sections.some((s) => s.id === sectionId),
      );

      if (parentMenu) {
        setSelectedMenuId(parentMenu.id);
      }

      setActiveSectionId(sectionId);
      scrollToElementWithLock(
        document.querySelector(`[data-section-id="${sectionId}"]`),
      );
    } else if (menuId) {
      setSelectedMenuId(menuId);
      scrollToElementWithLock(
        document.querySelector(`[data-menu-id="${menuId}"]`),
      );
    }
  }, [
    sectionTabs,
    menus,
    scrollToElementWithLock,
    setSelectedMenuId,
    setActiveSectionId,
  ]);
};
